import { useLocation } from "react-router-dom";
import BottomNavigationItem from "./BottomNavigationItem";
import {
  RecentServicesIcon,
  CustomersIcon,
  RegisterServiceIcon,
  ServiceWorkerDashboardIcon,
  UserPanelIcon,
} from "./ServiceWorkerIcons";
import "./bottomNavigation.scss";

// Route audit (verified against src/Router/AppRouter.jsx + src/Page/Index.js):
//   CONNECTED (route + page exist, reused verbatim):
//   - Recent Services ..... /LatestServices (existing services list page)
//   - Register Service .... /PlateCapture (camera entry before the existing
//                           ServiceOne form; active across the whole flow)
//   - SW Dashboard ........ /ServiceDashbord (existing worker dashboard)
//   - User Panel .......... /UserPanel (existing shared panel)
//   - Customers ........... /Customers (worker customer-management page)
//
// Customer-only pages are never linked from here: no "/", "/Cars", "/AddCars",
// "/notifications", or "/SelectService" targets. Shared list/panel pages
// (/LatestServices, /UserPanel) are reused, not customer-tab redirects.
export const RECENT_SERVICES_ROUTE = "/ServiceRecent";
export const REGISTER_SERVICE_ROUTE = "/PlateCapture";
export const SERVICE_WORKER_DASHBOARD_ROUTE = "/ServiceDashbord";
export const CUSTOMERS_ROUTE = "/Customers";
export const USER_PANEL_ROUTE = "/UserPanel";

// Single source of truth for the 5 worker tabs, in the requested order:
// سرویس‌های اخیر | ثبت سرویس | داشبورد سرویس کار | مشتری‌ها | پنل کاربری
// (RTL: first item is rightmost). Icon-only by design; names live in
// aria-label/title only — same pattern as the customer BottomNavigation.
const SERVICE_WORKER_NAV_ITEMS = [
  {
    key: "recent-services",
    to: RECENT_SERVICES_ROUTE,
    disabled: false,
    label: "سرویس‌های اخیر",
    title: "سرویس‌های اخیر",
    Icon: RecentServicesIcon,
    isActive: (pathname) => pathname.startsWith(RECENT_SERVICES_ROUTE),
  },
  {
    key: "register-service",
    to: REGISTER_SERVICE_ROUTE,
    disabled: false,
    label: "ثبت سرویس",
    title: "ثبت سرویس",
    Icon: RegisterServiceIcon,
    isActive: (pathname) =>
      pathname.startsWith("/PlateCapture") ||
      pathname.startsWith("/ServiceOne") ||
      pathname.startsWith("/ServiceTwo") ||
      pathname.startsWith("/ServiceThree") ||
      pathname.startsWith("/Periodic"),
  },
  {
    key: "service-worker-dashboard",
    to: SERVICE_WORKER_DASHBOARD_ROUTE,
    disabled: false,
    label: "داشبورد سرویس کار",
    title: "داشبورد سرویس کار",
    Icon: ServiceWorkerDashboardIcon,
    isActive: (pathname) => pathname.startsWith(SERVICE_WORKER_DASHBOARD_ROUTE),
  },
  {
    key: "customers",
    to: CUSTOMERS_ROUTE,
    disabled: false,
    label: "مشتری‌ها",
    title: "مشتری‌ها",
    Icon: CustomersIcon,
    isActive: (pathname) => pathname.startsWith(CUSTOMERS_ROUTE),
  },
  {
    key: "user-panel",
    to: USER_PANEL_ROUTE,
    disabled: false,
    label: "پنل کاربری",
    title: "پنل کاربری",
    Icon: UserPanelIcon,
    isActive: (pathname) => pathname.startsWith(USER_PANEL_ROUTE),
  },
];

// Prefixes that belong strictly to the Service Worker section (worker-only routes).
// Used by AppRouter as the base set; shared pages are handled separately so
// the customer bar is preserved for non-workers. Shared pages are still
// highlighted correctly wherever the worker bar is rendered.
export const SERVICE_WORKER_BOTTOM_NAV_PREFIXES = [
  SERVICE_WORKER_DASHBOARD_ROUTE,
  RECENT_SERVICES_ROUTE,
  CUSTOMERS_ROUTE,
  "/PlateCapture",
  "/ServiceOne",
  "/ServiceTwo",
  "/ServiceThree",
  "/Periodic",
];

// Shared routes that are reused by both sections. When the current user is a
// registered service worker (mechanic profile exists) or the last non-shared
// navigation was in the worker section, these routes must show the worker bar.
export const SERVICE_WORKER_SHARED_ROUTES = [RECENT_SERVICES_ROUTE, USER_PANEL_ROUTE];

export function isServiceWorkerSection(pathname = "") {
  return SERVICE_WORKER_BOTTOM_NAV_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix)
  );
}

export function isSharedServiceWorkerRoute(pathname = "") {
  return SERVICE_WORKER_SHARED_ROUTES.some((prefix) => pathname.startsWith(prefix));
}

// Separate bottom bar for the Service Worker section. Same shell as the
// customer bar (spacer + fixed nav + BottomNavigationItem slots + the shared
// bottomNavigation.scss), so size, spacing, animations, and appearance are
// identical — only the items/routes differ. Active tab is URL-driven.
function ServiceWorkerBottomNavigation() {
  const { pathname } = useLocation();

  return (
    <>
      {/* In-flow spacer: reserves the fixed bar's height so page
          content is never covered and no extra scroll is created. */}
      <div className="bottom-nav-spacer" aria-hidden="true" />
      <nav
        className="bottom-nav"
        dir="rtl"
        aria-label="ناوبری سرویس کار"
        data-testid="service-worker-bottom-nav"
      >
        <ul className="bottom-nav__list">
          {SERVICE_WORKER_NAV_ITEMS.map((item) => (
            <BottomNavigationItem
              key={item.key}
              itemKey={item.key}
              to={item.to}
              label={item.label}
              title={item.title}
              Icon={item.Icon}
              active={item.isActive(pathname)}
              disabled={item.disabled}
            />
          ))}
        </ul>
      </nav>
    </>
  );
}

export default ServiceWorkerBottomNavigation;
export { SERVICE_WORKER_NAV_ITEMS };
